import { Navigation } from "@/components/Navigation"
import { OpenPositions } from "@/components/OpenPositions"
import { Footer } from "@/components/Footer"
import { useEffect } from "react"
import { PageFAQ } from "@/components/PageFAQ"
import { careersFAQs } from "@/data/faqData"
import { Button } from "@/components/ui/button"
import { ArrowRight, Briefcase, GraduationCap, Users2 } from "lucide-react"

import { SEO } from "@/components/SEO"

const benefits = [
  {
    icon: Briefcase,
    title: "Global Impact",
    description: "Work on mission-critical projects for Fortune 500 clients.",
  },
  {
    icon: GraduationCap,
    title: "Continuous Evolution",
    description: "Unlimited access to learning resources and certifications.",
  },
  {
    icon: Users2,
    title: "Vibrant Culture",
    description: "Join a diverse community of innovators and problem solvers.",
  },
]

const Careers = () => {
  useEffect(() => {
    if (window.location.hash === "#open-positions") {
      setTimeout(() => {
        document
          .getElementById("open-positions")
          ?.scrollIntoView({ behavior: "smooth" })
      }, 100)
    }
  }, [])

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <SEO
        title="Careers at RSA Tech Group | Join the Cognitive Revolution"
        description="At RSA Tech Group, we're not just building software; we're crafting the neural infrastructure of tomorrow's enterprises."
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
      />
      <Navigation />

      {/* ───── S1 Hero - Dark ───── */}
      <section className="relative pt-40 pb-24 overflow-hidden min-h-[80vh] flex items-center justify-center bg-[#0f1629]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(28,43,110,0.5),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.3),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.08),transparent_70%)]" />

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

        <div className="section-container relative z-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#FDA400]" />
            <span className="text-[#FDA400] text-xs font-semibold uppercase tracking-[0.2em]">
              Careers
            </span>
            <div className="h-px w-10 bg-[#FDA400]" />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 max-w-4xl mx-auto">
            Architect the Future of{" "}
            <span className="text-[#FDA400]">Intelligence</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 leading-relaxed font-light max-w-2xl mx-auto mb-10">
            Build your career across RSA Tech Group's portfolio — from enterprise managed services at Connexr to AI innovation at LeoRix AI.
          </p>

          <Button
            size="lg"
            className="bg-[#FDA400] hover:bg-[#FDA400]/90 text-[#1C2B6E] font-bold text-sm px-8 py-6 h-auto rounded-lg shadow-lg shadow-[#FDA400]/20 transition-all"
            onClick={() =>
              document
                .getElementById("open-positions")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            Explore Open Roles <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* ───── S2 Why RSA Tech Group - Light + Dot Grid ───── */}
      <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
        {/* Dot grid bg */}
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Why RSA Tech Group
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              Build What <span className="text-[#FDA400]">Matters</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
              >
                {/* Top accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                {/* Glow */}
                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-[#1C2B6E]/[0.08] flex items-center justify-center mb-6 group-hover:bg-[#1C2B6E] transition-colors duration-300">
                    <benefit.icon className="w-6 h-6 text-[#1C2B6E] group-hover:text-[#FDA400] transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1C2B6E] mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───── S3 Open Positions - Dark ───── */}
      <OpenPositions />

      {/* ───── S4 FAQ - Light ───── */}
      <PageFAQ
        faqs={careersFAQs}
        title="Careers FAQs"
        subtitle="Common questions about working at RSA Tech Group"
        variant="muted"
      />
      <Footer />
    </div>
  )
}

export default Careers
